'use client';

import { c as _c } from "react/compiler-runtime";
import type { ProfilePortfolioRealtimeEvidence, UserPortfolioRealtimeRecoveryReason, UserPortfolioSnapshotWire } from '@morfi/backend-sdk-v2/client';
import { useEffect, useRef } from 'react';
import { USDC_DUST_THRESHOLD_D6 } from '@/config/solana';
import { logger } from '@/lib/telemetry/logger';
import { type ProfileObservabilityMessage, type ProfileReconciliationEvidenceReason, recordProfileObservability, recordProfileReconciliation, recordProfileSnapshot, recordProfileValuation, recordProfileWireHandoff } from '../profile-portfolio-evidence';
import { userPortfolioPositionDisplayMark, userPortfolioSummary } from './valuation';
export interface UseUserPortfolioSnapshotEvidenceArgs {
  readonly isError: boolean;
  readonly isFetching: boolean;
  readonly isRealtimeReady: boolean;
  readonly quoteMint: string;
  readonly snapshot: UserPortfolioSnapshotWire | null;
}
export function useUserPortfolioSnapshotEvidence(t0) {
  const $ = _c(11);
  const {
    isError,
    isFetching,
    isRealtimeReady,
    quoteMint,
    snapshot
  } = t0;
  const requestOrdinal = useRef(0);
  const requestActive = useRef(false);
  const lastValuationKey = useRef("");
  let t1;
  let t2;
  if ($[0] !== isError || $[1] !== isFetching || $[2] !== isRealtimeReady || $[3] !== snapshot) {
    t1 = () => {
      if (isFetching && !requestActive.current) {
        requestActive.current = true;
        requestOrdinal.current = requestOrdinal.current + 1;
        recordProfileSnapshot({
          phase: "snapshot_loading_started",
          requestOrdinal: requestOrdinal.current
        });
        return;
      }
      if (isFetching || !requestActive.current) {
        return;
      }
      if (!isError && snapshot !== null && !isRealtimeReady) {
        return;
      }
      requestActive.current = false;
      if (isError || snapshot === null) {
        const reason = isError ? "transport" : "invalid_snapshot";
        logger.warn("profile_valuation.snapshot_failed", {
          reason
        });
        recordProfileSnapshot({
          phase: "snapshot_failed",
          requestOrdinal: requestOrdinal.current,
          reason
        });
        return;
      }
      recordSnapshotSucceeded(requestOrdinal.current, snapshot);
    };
    t2 = [isError, isFetching, isRealtimeReady, snapshot];
    $[0] = isError;
    $[1] = isFetching;
    $[2] = isRealtimeReady;
    $[3] = snapshot;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[6] !== isRealtimeReady || $[7] !== quoteMint || $[8] !== snapshot) {
    t3 = () => {
      if (!isRealtimeReady || snapshot === null) {
        return;
      }
      const valuationKey = valuationEvidenceKey(snapshot);
      if (lastValuationKey.current === valuationKey) {
        return;
      }
      lastValuationKey.current = valuationKey;
      const marks = snapshot.positions.map(position => userPortfolioPositionDisplayMark(position, snapshot.poolSnapshotsByMarket[position.market]));
      const summary = userPortfolioSummary(snapshot, snapshot.wallets, quoteMint);
      recordProfileValuation({
        positionCount: snapshot.positions.length,
        pricedPositionCount: marks.filter(_temp).length,
        unrealizedAvailableCount: marks.filter(_temp2).length,
        dustPositionCount: marks.filter(_temp3).length,
        holdingsAvailable: summary.holdingsD6 !== null,
        cashAvailable: summary.cashD6 !== null,
        totalAvailable: summary.totalD6 !== null
      });
    };
    t4 = [isRealtimeReady, quoteMint, snapshot];
    $[6] = isRealtimeReady;
    $[7] = quoteMint;
    $[8] = snapshot;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useEffect(t3, t4);
}
/** Feeds SDK realtime evidence into the local E2E rings; Better Stack logging is the SDK translator's. */
function _temp3(t0) {
  const {
    currentValueD6: currentValueD6_0
  } = t0;
  return currentValueD6_0 !== null && currentValueD6_0 < BigInt(USDC_DUST_THRESHOLD_D6);
}
function _temp2(t0) {
  const {
    unrealisedPnlD6
  } = t0;
  return unrealisedPnlD6 !== null;
}
function _temp(t0) {
  const {
    currentValueD6
  } = t0;
  return currentValueD6 !== null;
}
export function recordUserPortfolioRealtimeEvidence(evidence: ProfilePortfolioRealtimeEvidence): void {
  switch (evidence.event) {
    case 'wallet_interest_ready':
      recordProfileWireHandoff({
        phase: 'wallet_interest_ready',
        walletCount: evidence.walletCount
      });
      return;
    case 'reconciliation_scheduled':
      recordProfileObservability({
        message: 'profile_valuation.reconciliation_scheduled',
        reason: observabilityEvidenceReason(evidence.reason)
      });
      recordReconciliation('scheduled', evidence.reason);
      return;
    case 'reconciliation_coalesced':
      recordReconciliation('coalesced', evidence.reason);
      return;
    case 'reconciliation_completed':
      recordReconciliation('completed', evidence.reason);
      return;
    case 'reconciliation_failed':
      recordReconciliation('failed', evidence.reason);
      return;
    case 'resolution_coalesced':
      recordProfileReconciliation({
        phase: 'coalesced',
        reason: 'resolution_event',
        pending: true
      });
      return;
    case 'event_failure':
      recordProfileObservability({
        message: eventFailureMessage(evidence.reason),
        reason: evidence.reason
      });
      return;
    case 'filter_install_failed':
      recordProfileObservability({
        message: 'profile_valuation.filter_install_failed',
        reason: evidence.reason,
        rateFilterCount: evidence.rateFilterCount,
        ...(evidence.lifecycleFilterCount === undefined ? {} : {
          lifecycleFilterCount: evidence.lifecycleFilterCount
        })
      });
      return;
    case 'positive_positions_without_rate_filters':
      recordProfileObservability({
        message: 'profile_valuation.positive_positions_without_rate_filters',
        positivePositionCount: evidence.positivePositionCount,
        rateFilterCount: evidence.rateFilterCount,
        lifecycleFilterCount: evidence.lifecycleFilterCount,
        reason: evidence.reason
      });
      return;
    case 'market_transition_refetch_failed':
      return;
  }
}
function recordReconciliation(phase: 'scheduled' | 'coalesced' | 'completed' | 'failed', reason: UserPortfolioRealtimeRecoveryReason): void {
  recordProfileReconciliation({
    phase,
    reason: reconciliationEvidenceReason(reason),
    pending: phase === 'scheduled' || phase === 'coalesced'
  });
}
function reconciliationEvidenceReason(reason: UserPortfolioRealtimeRecoveryReason): ProfileReconciliationEvidenceReason {
  switch (reason) {
    case 'wallet_claim_changed':
    case 'wallet_position_changed':
      return 'wallet_position_event';
    case 'wallet_quote_balance_changed':
      return 'wallet_quote_balance_event';
    case 'lifecycle_transition':
      return 'lifecycle_event';
    case 'resolution_transition':
      return 'resolution_event';
    case 'event_identity_invalid':
      return 'identity_change';
    case 'event_payload_incomplete':
      return 'payload_incomplete';
    case 'sequence_gap':
      return 'sequence_gap';
    case 'consumer_recreated':
      return 'reconnect';
    case 'filter_install_failed':
    case 'replay_unavailable':
      return 'bounded_replay_failed';
    case 'wallet_set_changed':
      return 'wallet_set_changed';
  }
}
function observabilityEvidenceReason(reason: UserPortfolioRealtimeRecoveryReason): string {
  return reason === 'wallet_claim_changed' ? 'wallet_position_changed' : reason;
}
function eventFailureMessage(reason: 'event_identity_invalid' | 'event_payload_incomplete' | 'sequence_gap'): ProfileObservabilityMessage {
  if (reason === 'event_identity_invalid') return 'profile_valuation.event_identity_invalid';
  if (reason === 'event_payload_incomplete') return 'profile_valuation.event_payload_incomplete';
  return 'profile_valuation.sequence_gap';
}
function recordSnapshotSucceeded(requestOrdinal: number, snapshot: UserPortfolioSnapshotWire): void {
  const entries = Object.values(snapshot.poolSnapshotsByMarket);
  recordProfileSnapshot({
    phase: 'snapshot_ready',
    requestOrdinal,
    walletCount: snapshot.wallets.length,
    positionCount: snapshot.positions.length,
    completePoolCount: entries.filter(({
      status
    }) => status === 'complete').length,
    incompletePoolCount: entries.filter(({
      status
    }) => status === 'incomplete').length,
    streamSequence: snapshot.recovery.streamSequence
  });
}
function valuationEvidenceKey(snapshot: UserPortfolioSnapshotWire): string {
  return JSON.stringify({
    marketSequences: snapshot.recovery.marketSequences,
    outcomeSequences: snapshot.recovery.outcomeSequences,
    positionSequences: snapshot.recovery.positionSequences,
    positions: snapshot.positions.map(position => [position.balanceD6, position.claimRightsBalanceD6, position.remainingCostBasisGrossD6, position.realizedPnlGrossD6]),
    quoteBalances: snapshot.quoteBalances.map(({
      balanceD6
    }) => balanceD6)
  });
}